import { Trans } from '@lingui/react';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { Box } from '@startuptools/ui/Box';
import { ErrorDisplay } from '../../../components/error-display/ErrorDisplay';
import { useStepperStepContent } from '../../../components/steppers/common/StepperStepContentContext';
import { Button } from '@startuptools/ui/Button';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { Card } from '@startuptools/ui/Card';
import { WarrantProgramDocument } from '../../stock-options/WarrantProgram.graphql';
import { ReactNode } from 'react';
import { GqlBoardsStatementOption, GqlCreateIdentityInput } from '../../../graphql/base-types.graphql';
import { DateTime } from 'luxon';
import { Control, Controller, SubmitHandler, UseFormHandleSubmit } from 'react-hook-form';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
import { BoardMembersTable } from './BoardMembersTable';

export type FormValues = {
  boardsStatementOption: GqlBoardsStatementOption | null;
  boardsStatementDate: DateTime | null;
  boardsStatementSv: string | undefined;
  boardsStatementEn: string | undefined;
};

const prevFY = DateTime.local().minus({ years: 2 }).toFormat('yyyy');
const currFY = DateTime.local().minus({ years: 1 }).toFormat('yyyy');
export const defaultStatementSv = `Bolaget har ökat omsättningen med XX procent från XX miljoner kr under ${prevFY} till XX miljoner under perioden ${currFY}. Antalet anställda har ökat från XX för helåret ${prevFY} till XX vid utgången av ${currFY}. Kassan per ${prevFY}-12-31 uppgår till XXXXX kr. I januari ${currFY} ersatte NN1 NN2 som VD för företaget. NN3 invaldes vid bolagsstämma ${currFY}-XX-XX i styrelsen som dess ordförande.`;
export const defaultStatementEn = `The company increased its turnover by XX percent from XX million in ${prevFY} to XX million during the period ${currFY}. The number of employees increased from XX for the full year of ${prevFY} to XX by the end of ${currFY}. The cash as of ${prevFY}-12-31 amounts to XXXXX kr. In January ${currFY}, NN1 replaced NN2 as CEO of the company. NN3 was elected at the Annual General Meeting held on ${currFY}-XX-XX as the Chairman of the Board.`;

export const StepBoardStatement = ({
  control,
  handleSubmit,
  onSubmit,
  loading,
  secondaryLang,
  noBoardsStatement,
  showNone,
  showWaivedByShareholders,
  showWaivedByAuth,
  waiverLabel,
  boardMemberIdentities,
  handleCreateBoardMember,
  handleDeleteBoardMember,
  currentBoardsStateOptions,
}: {
  control: Control<FormValues>;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  onSubmit: SubmitHandler<FormValues>;
  loading: boolean;
  secondaryLang: boolean;
  noBoardsStatement: boolean;
  showNone: boolean;
  showWaivedByShareholders: boolean;
  showWaivedByAuth: boolean;
  waiverLabel: ReactNode;
  boardMemberIdentities: { id: string; identity: DisplayIdentity }[];
  handleCreateBoardMember: (identity: GqlCreateIdentityInput) => Promise<unknown>;
  handleDeleteBoardMember: (id: string) => Promise<unknown>;
  currentBoardsStateOptions: GqlBoardsStatementOption;
}) => {
  const { validatorStatus } = useStepperStepContent();

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Typography level="h4">
            <Trans id="Shall the board make statements regarding important events?" />
          </Typography>

          <Controller
            control={control}
            name="boardsStatementOption"
            render={({ field }) => (
              <RadioGroup {...field}>
                {showNone && (
                  <Radio
                    value={GqlBoardsStatementOption.None}
                    label={
                      <Trans id="Do not create a statement. All shareholders are represented at the shareholders' meeting" />
                    }
                  />
                )}
                {showWaivedByShareholders && <Radio value={GqlBoardsStatementOption.Waiver} label={waiverLabel} />}
                <Radio
                  value={GqlBoardsStatementOption.Produce}
                  label={<Trans id="Create a board statement and upload the auditor's statement" />}
                />
                {showWaivedByAuth && (
                  <Radio
                    value={GqlBoardsStatementOption.WaivedByAuth}
                    label={
                      <Trans id="The authorization makes it possible to do a share issue without the board statement" />
                    }
                  />
                )}
                {!noBoardsStatement && (
                  <Radio
                    value={GqlBoardsStatementOption.ProducedExternally}
                    label={<Trans id="There is a board statement which has been created outside of the system" />}
                  />
                )}
              </RadioGroup>
            )}
          />

          {currentBoardsStateOptions === GqlBoardsStatementOption.Produce && (
            <>
              <Controller
                control={control}
                name="boardsStatementDate"
                render={({ field }) => (
                  <FormControl sx={{ maxWidth: 300 }}>
                    <FormLabel>
                      <Trans id="Date of Board Statement" />
                    </FormLabel>
                    <DateInput {...field} />
                  </FormControl>
                )}
              />

              <Alert>
                <Trans id="This is an example of what the board's statement may look like. There are no formal requirements of what the board statement should look like; what is important is that the company's auditor approves of it. Therefore, you should start by reviewing this with the auditor. Below you find an example of what the statement could look like." />
              </Alert>

              <Controller
                control={control}
                name="boardsStatementSv"
                render={({ field: { onChange, ...field } }) => (
                  <FormControl>
                    <FormLabel>
                      <Trans id="Board Statement (Swedish)" />
                    </FormLabel>
                    <RichTextInput value={field.value} onChange={onChange} />
                  </FormControl>
                )}
              />

              {secondaryLang && (
                <Controller
                  control={control}
                  name="boardsStatementEn"
                  render={({ field: { onChange, ...field } }) => (
                    <FormControl>
                      <FormLabel>
                        <Trans id="Board Statement (English)" />
                      </FormLabel>
                      <RichTextInput value={field.value} onChange={onChange} />
                    </FormControl>
                  )}
                />
              )}

              <Card>
                <Typography level="title-lg">
                  <Trans id="Who is signing?" />
                </Typography>

                <BoardMembersTable
                  boardMembers={boardMemberIdentities}
                  onCreate={handleCreateBoardMember}
                  onDelete={handleDeleteBoardMember}
                  refetchQueries={[WarrantProgramDocument]}
                />
              </Card>
            </>
          )}

          {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

          <Box>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
    </>
  );
};
